import React, { useMemo, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronDown, ChevronRight, X } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import { TimesheetRow } from '../../../../model/timesheet.model';
import HourCell, { CellNav, formatHours, formatTotal } from './HourCell';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const groupKey = (row: TimesheetRow) => `${row.clientName}|${row.projectName}`;

const sum = (values: number[]) => values.reduce((acc, value) => acc + value, 0);

interface Group {
  key: string;
  clientName: string;
  projectName: string;
  projectColor: string;
  rows: TimesheetRow[];
}

interface WeekGridProps {
  rows: TimesheetRow[];
  days: Date[];
  /** Index of today inside this week, or -1 when the week is not the current one */
  todayIndex: number;
  locked: boolean;
  onChangeHours: (rowId: string, day: number, value: number) => void;
  onRemoveRow: (rowId: string) => void;
  addProjectSlot: React.ReactNode;
}

const WeekGrid: React.FC<WeekGridProps> = ({
  rows,
  days,
  todayIndex,
  locked,
  onChangeHours,
  onRemoveRow,
  addProjectSlot,
}) => {
  const [collapsed, setCollapsed] = useState<string[]>([]);
  const inputs = useRef(new Map<string, HTMLInputElement | null>());

  const groups = useMemo(() => {
    const byKey = new Map<string, Group>();
    rows.forEach(row => {
      const key = groupKey(row);
      const existing = byKey.get(key);
      if (existing) {
        existing.rows.push(row);
      } else {
        byKey.set(key, {
          key,
          clientName: row.clientName,
          projectName: row.projectName,
          projectColor: row.projectColor,
          rows: [row],
        });
      }
    });
    return Array.from(byKey.values());
  }, [rows]);

  /** Rows in display order — the order arrow keys walk through. */
  const navigableIds = useMemo(
    () =>
      groups
        .filter(group => !collapsed.includes(group.key))
        .flatMap(group => group.rows.map(row => row.id)),
    [groups, collapsed],
  );

  const focusCell = (rowId: string, day: number) => {
    const input = inputs.current.get(`${rowId}:${day}`);
    input?.focus();
  };

  const navigate = (rowId: string, day: number, direction: CellNav) => {
    const index = navigableIds.indexOf(rowId);
    if (index === -1) return;

    switch (direction) {
      case 'up':
        if (index > 0) focusCell(navigableIds[index - 1], day);
        break;
      case 'down':
        if (index < navigableIds.length - 1) focusCell(navigableIds[index + 1], day);
        break;
      case 'left':
        if (day > 0) focusCell(rowId, day - 1);
        else if (index > 0) focusCell(navigableIds[index - 1], DAY_LABELS.length - 1);
        break;
      case 'right':
        if (day < DAY_LABELS.length - 1) focusCell(rowId, day + 1);
        else if (index < navigableIds.length - 1) focusCell(navigableIds[index + 1], 0);
        break;
      default:
        break;
    }
  };

  const toggleGroup = (key: string) =>
    setCollapsed(prev => (prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]));

  const dayTotals = DAY_LABELS.map((_, day) => sum(rows.map(row => row.hours[day])));
  const weekTotal = sum(dayTotals);

  const isWeekend = (day: number) => day >= 5;
  const cellTone = (day: number) =>
    cn(
      day === todayIndex && 'bg-accent/60',
      isWeekend(day) && day !== todayIndex && 'bg-muted/40',
    );

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[760px] border-separate border-spacing-0">
        <thead>
          <tr>
            <th className="sticky left-0 z-20 border-b border-border bg-card px-3 py-2 text-left text-xs font-normal uppercase tracking-wide text-muted-foreground">
              Project · Work type
            </th>
            {days.map((date, day) => (
              <th
                key={day}
                className={cn(
                  'w-[68px] border-b border-border px-1 py-2 text-center font-normal',
                  cellTone(day),
                )}
              >
                <div
                  className={cn(
                    'text-xs uppercase tracking-wide',
                    day === todayIndex ? 'text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {DAY_LABELS[day]}
                </div>
                <div
                  className={cn(
                    'text-sm tabular-nums',
                    day === todayIndex ? 'font-medium text-foreground' : 'text-muted-foreground',
                  )}
                >
                  {date.getDate()}
                </div>
              </th>
            ))}
            <th className="w-[76px] border-b border-border px-2 py-2 text-right text-xs font-normal uppercase tracking-wide text-muted-foreground">
              Total
            </th>
          </tr>
        </thead>

        <tbody>
          {groups.map(group => {
            const isCollapsed = collapsed.includes(group.key);
            const groupDayTotals = DAY_LABELS.map((_, day) => sum(group.rows.map(row => row.hours[day])));

            return (
              <React.Fragment key={group.key}>
                <tr className="group/project">
                  <td className="sticky left-0 z-10 border-b border-border/60 bg-card px-3 py-1.5">
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.key)}
                      className="flex w-full items-center gap-2 text-left"
                    >
                      {isCollapsed ? (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      )}
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-sm"
                        style={{ backgroundColor: group.projectColor }}
                      />
                      <span className="truncate text-sm">
                        <span className="text-muted-foreground">{group.clientName}</span>
                        <span className="mx-1.5 text-muted-foreground/50">·</span>
                        <span className="font-medium">{group.projectName}</span>
                      </span>
                    </button>
                  </td>
                  {groupDayTotals.map((total, day) => (
                    <td
                      key={day}
                      className={cn(
                        'border-b border-border/60 px-1 py-1.5 text-center text-xs tabular-nums',
                        isCollapsed ? 'text-foreground' : 'text-muted-foreground/60',
                        cellTone(day),
                      )}
                    >
                      {formatHours(total) || ''}
                    </td>
                  ))}
                  <td className="border-b border-border/60 px-2 py-1.5 text-right text-sm tabular-nums">
                    {formatHours(sum(groupDayTotals)) || ''}
                  </td>
                </tr>

                {!isCollapsed &&
                  group.rows.map(row => {
                    const rowTotal = sum(row.hours);
                    const removable = !locked && rowTotal === 0;

                    return (
                      <tr key={row.id} className="group/row">
                        <td className="sticky left-0 z-10 border-b border-border/40 bg-card py-0.5 pl-[38px] pr-3">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm text-foreground/90">{row.workType}</span>
                            {!row.billable && (
                              <span className="shrink-0 rounded border border-border px-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                                non-billable
                              </span>
                            )}
                            {removable && (
                              <button
                                type="button"
                                onClick={() => onRemoveRow(row.id)}
                                title="Remove from sheet"
                                className="ml-auto shrink-0 rounded p-0.5 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground group-hover/row:opacity-100"
                              >
                                <X className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </td>
                        {row.hours.map((value, day) => (
                          <td
                            key={day}
                            className={cn('border-b border-border/40 px-0.5', cellTone(day))}
                          >
                            <HourCell
                              ref={element => {
                                inputs.current.set(`${row.id}:${day}`, element);
                              }}
                              value={value}
                              disabled={locked}
                              isToday={day === todayIndex}
                              isWeekend={isWeekend(day)}
                              onCommit={next => onChangeHours(row.id, day, next)}
                              onNav={direction => navigate(row.id, day, direction)}
                            />
                          </td>
                        ))}
                        <td className="border-b border-border/40 px-2 text-right text-sm tabular-nums text-muted-foreground">
                          {formatHours(rowTotal) || ''}
                        </td>
                      </tr>
                    );
                  })}
              </React.Fragment>
            );
          })}

          {!locked && (
            <tr>
              <td className="sticky left-0 z-10 bg-card px-3 py-1.5">{addProjectSlot}</td>
              <td colSpan={DAY_LABELS.length + 1} />
            </tr>
          )}
        </tbody>

        <tfoot>
          <tr>
            <td className="sticky left-0 z-10 border-t border-border bg-card px-3 py-2 text-sm font-medium">
              Total
            </td>
            {dayTotals.map((total, day) => (
              <td
                key={day}
                className={cn(
                  'border-t border-border px-1 py-2 text-center text-sm font-medium tabular-nums',
                  total === 0 && 'text-muted-foreground/40',
                  cellTone(day),
                )}
              >
                {formatTotal(total)}
              </td>
            ))}
            <td className="border-t border-border px-2 py-2 text-right text-sm font-semibold tabular-nums">
              {formatTotal(weekTotal)}
            </td>
          </tr>
          <tr>
            <td className="sticky left-0 z-10 bg-card" />
            {/* Only the actionable state gets a warning — a day with nothing logged. */}
            {dayTotals.map((total, day) => {
              const missing = total === 0 && !isWeekend(day);

              return (
                <td key={day} className={cn('px-1 pb-2 text-center', cellTone(day))}>
                  {missing && (
                    <AlertTriangle
                      className="mx-auto h-3 w-3 text-amber-600 dark:text-amber-500"
                      aria-label="No hours logged"
                    />
                  )}
                  {total > 0 && (
                    <Check
                      className="mx-auto h-3 w-3 text-emerald-600/70 dark:text-emerald-500/70"
                      aria-label={`${formatHours(total)} h`}
                    />
                  )}
                </td>
              );
            })}
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
};

export default WeekGrid;
